import { immerable } from "@/app/lib/system/immer-wrapper";
// my imports:
import WordModule from "@/app/lib/game-state/word-module";
import LetterModule from "@/app/lib/game-state/letter-module";
import * as Util from "@/app/lib/system/util";
import { GameMode } from "@/app/lib/game-state/game-types";

// common fields and methods shared by all puzzle types
export abstract class AbstractGameState<TPuzzleData, TSnapshot> {
    [immerable] = true

    abstract name: GameMode;
    words: WordModule[] = [];
    ingredients: string[] = [];
    usedSolutions: string[] = [];
    completed: boolean = false;
    victorious: boolean = false;
    version: string;

    // putting a constructor in parent class forces us to call super() in child constructors
    constructor(version: string) {
        this.version = version;
    }

    // shared cleanup for every puzzle
    // calls each puzzle's specific wipe needs
    wipe() : void {
        this.words = [];
        this.ingredients = [];
        this.completed = false;
        this.victorious = false;

        // subclass cleanup hook
        this.wipeSpecific();
    }
    // note how this is called by wipe
    protected abstract wipeSpecific(): void;

    // Build puzzle defined by children
    // Likewise, for snapshots
    abstract buildPuzzle(puzzleData: TPuzzleData) : void;
    abstract exportSnapshot() : TSnapshot;
    abstract importSnapshot(snapshot: TSnapshot) : void;
    abstract freezeInputs() : boolean;

    // validating a word will depend on how puzzle handles solutions
    abstract validateWord(wordIndex: number) : boolean;

    validateLetter(char: string) : boolean {
        return this.ingredients.includes(char);
    }

    // reveal logic is also dependent on puzzle type
    abstract reveal(trueVictory: boolean) : void;

    addNewWord(word: string) : void {
        const wordModule: WordModule = new WordModule(word);
        this.words.push(wordModule);
        const index = this.words.length - 1;
        wordModule.setIndex(index);
        // if a word began as all givens it should be in used solutions
        // it will be marked solve by the word module constructor
        if (wordModule.solved) {
            this.usedSolutions.push(wordModule.stringifyState().toLowerCase());
        }
    }

    getLetter(wordIndex: number, letterPosition: number) : string {
        return this.words[wordIndex].letters[letterPosition].character;
    }

    shuffle() {
        this.ingredients = Util.shuffle(this.ingredients);
    }

    checkVictory(trueVictory: boolean) {
        for (const word of this.words) {
            // added a special case for if a word is all givens, mark it as solved
            // filter out the givens
            const givens: LetterModule[] = word.letters.filter(
                letterModule => letterModule.type === 'given');
            // are they equivalent to the word length
            if (givens.length === word.letters.length) {
                word.solved = true;
            }
            if (!word.solved) {
                return;
            }
        }
        this.markCompleted(trueVictory);
        return;
    }

    markCompleted(trueVictory: boolean) {
        this.completed = true;
        this.victorious = trueVictory;
    }
}